'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HiArrowsRightLeft } from 'react-icons/hi2';
import { FaWallet, FaServer, FaUsers, FaTasks, FaCheckCircle } from 'react-icons/fa';

import useApiQuery from 'lib/api/useApiQuery';

import styles from './StatsGrid.module.css';
import StatCard from '../StatCard';

interface ManagementStats {
  activeProviders: number;
  activeClients: number;
  totalTasks: number;
  successRate: number;
}

const StatsGrid: React.FC = () => {
  const { data: stats, isLoading: isStatsLoading } = useApiQuery('stats', {
    queryOptions: {
      refetchInterval: 60000,
    },
  });

  const { data: managementStats, isLoading: isManagementLoading } = useQuery<ManagementStats>({
    queryKey: ['management_stats'],
    queryFn: async () => {
      const res = await fetch('https://management-backend.opengpu.network/api/dashboard/stats');
      if (!res.ok) throw new Error('Failed to fetch management stats');
      const json = await res.json();
      return json.data;
    },
    refetchInterval: 60000,
  });

  const [ opacity, setOpacity ] = useState(1);
  const prevDataRef = useRef<string>('');

  useEffect(() => {
    if (stats) {
      const currentData = JSON.stringify(stats);
      if (prevDataRef.current && prevDataRef.current !== currentData) {
        setOpacity(0);
        setTimeout(() => setOpacity(1), 50);
      }
      prevDataRef.current = currentData;
    }
  }, [ stats ]);

  const formatNumber = (value: string | number | undefined) => {
    if (!value) {
      return '0';
    }
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (num >= 1000000) {
      return `${ Math.round(num / 1000000) }M`;
    }
    if (num >= 1000) {
      return `${ Math.round(num / 1000) }K`;
    }
    return num.toLocaleString();
  };

  return (
    <div className={ styles.container } style={{ opacity, transition: 'opacity 0.3s ease-in-out' }}>
      <div className={ styles.grid }>
        <div className={ styles.groupLeft }>
          <StatCard
            label="Total transactions"
            value={ stats?.total_transactions ? Number(stats.total_transactions).toLocaleString() : '0' }
            icon={ <HiArrowsRightLeft size={ 24 } color="#8b5cf6"/> }
            isLoading={ isStatsLoading }
          />
          <StatCard
            label="Total addresses"
            value={ stats?.total_addresses ? Number(stats.total_addresses).toLocaleString() : '0' }
            icon={ <FaWallet size={ 22 } color="#3b82f6"/> }
            isLoading={ isStatsLoading }
          />
        </div>
        <div className={ styles.groupRight }>
          <StatCard
            label="Total Providers"
            value={ managementStats?.activeProviders ? formatNumber(managementStats.activeProviders) : '0' }
            icon={ <FaServer size={ 22 } color="#10b981"/> }
            isLoading={ isManagementLoading }
          />
          <StatCard
            label="Total Clients"
            value={ managementStats?.activeClients ? formatNumber(managementStats.activeClients) : '0' }
            icon={ <FaUsers size={ 22 } color="#f59e0b"/> }
            isLoading={ isManagementLoading }
          />
          <StatCard
            label="Total Tasks"
            value={ managementStats?.totalTasks ? formatNumber(managementStats.totalTasks) : '0' }
            icon={ <FaTasks size={ 22 } color="#6366f1"/> }
            isLoading={ isManagementLoading }
          />
          <StatCard
            label="Task Completion"
            value={ managementStats?.successRate !== undefined ? `${ managementStats.successRate.toFixed(2) }%` : '0%' }
            icon={ <FaCheckCircle size={ 22 } color="#22c55e"/> }
            isLoading={ isManagementLoading }
          />
        </div>
      </div>
    </div>
  );
};

export default StatsGrid;
